import type { Device } from './Device';
import type { Site } from './Site';

export interface WeatherLocation {
  lat: number;
  lon: number;
  // De dónde salió: GPS del equipo, marcada a mano en el dispositivo, o
  // heredada del campo al que pertenece.
  source: 'gps' | 'manual' | 'site';
  siteName?: string;
}

// El clima y el pronóstico de cada dispositivo son los de SU lugar: su propia
// ubicación (GPS o manual) y, si no tiene, la de su campo. Nunca la del
// usuario — puede tener estaciones en campos distintos.
export function weatherLocationFor(device: Pick<Device, 'lat' | 'lng' | 'locationSource' | 'siteId'>, sites: Site[]): WeatherLocation | null {
  if (device.lat !== null && device.lng !== null) {
    return { lat: device.lat, lon: device.lng, source: device.locationSource === 'gps' ? 'gps' : 'manual' };
  }
  const site = device.siteId ? sites.find((s) => s.id === device.siteId) : undefined;
  if (site && site.lat !== null && site.lng !== null) {
    return { lat: site.lat, lon: site.lng, source: 'site', siteName: site.name };
  }
  return null;
}

export const LOCATION_SOURCE_LABEL: Record<WeatherLocation['source'], string> = {
  gps: 'GPS del equipo',
  manual: 'marcada en el mapa',
  site: 'ubicación del campo',
};

// Coordenadas escritas o pegadas a mano: "-34.6037, -58.3816", "-34.6 -58.4"
// o lo que copia Google Maps ("-34.603722, -58.381592"). Acepta coma decimal
// si los números van separados por espacio o punto y coma. null si no son
// coordenadas válidas.
export function parseCoordinates(text: string): { lat: number; lng: number } | null {
  const clean = text.trim();
  if (!clean) return null;
  let parts: string[];
  if (/[;]/.test(clean)) parts = clean.split(/\s*;\s*/);
  else if (/^\S+\s*,\s*\S+$/.test(clean) && clean.split(',').length === 2) parts = clean.split(/\s*,\s*/);
  else parts = clean.split(/\s+/);
  if (parts.length !== 2) return null;
  const [lat, lng] = parts.map((p) => Number(p.replace(',', '.')));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}
